/**
 * Goal types, qualitative progress enum and the progress formula —
 * Document 6 §14.
 */

import type { MetricKey } from './metrics.js';

export const GOAL_TYPES = [
  'weight',
  'waist',
  'hip',
  'chest',
  'right_arm',
  'left_arm',
  'right_thigh',
  'left_thigh',
  'body_fat',
  'muscle',
  'flexibility',
  'balance',
  'core_control',
  'posture',
  'conditioning',
  'custom',
  'custom_metric',
] as const;

export type GoalType = (typeof GOAL_TYPES)[number];

export const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  weight: 'Kilo',
  waist: 'Bel Çevresi',
  hip: 'Kalça Çevresi',
  chest: 'Göğüs Çevresi',
  right_arm: 'Sağ Kol Çevresi',
  left_arm: 'Sol Kol Çevresi',
  right_thigh: 'Sağ Bacak Çevresi',
  left_thigh: 'Sol Bacak Çevresi',
  body_fat: 'Yağ Oranı',
  muscle: 'Kas Oranı',
  flexibility: 'Esneklik',
  balance: 'Denge',
  core_control: 'Core Kontrolü',
  posture: 'Postür',
  conditioning: 'Genel Kondisyon',
  custom: 'Özel Hedef',
  custom_metric: 'Özel Ölçüm',
};

/**
 * Which measurement a goal reads its current value from — added 10 September
 * 2026.
 *
 * A goal on weight, waist, hip, chest, an arm, a thigh, body fat or muscle is
 * tracked by a number the studio already records every visit. Asking the
 * instructor to retype it made "Güncel" mean "whatever was last typed into
 * this form", which went stale the moment a new measurement was taken and
 * quietly made every progress percentage wrong. It now means what it says:
 * the latest measurement.
 *
 * The goal types absent from this map — flexibility, balance, core control,
 * posture, conditioning, custom — have no measurement behind them. They keep
 * a typed current value, or none at all.
 *
 * `custom_metric` also tracks a measurement, but not through this map: it
 * has no fixed MetricKey, only a per-student label (`customMetricLabel` on
 * the goal, matched against `customMetricsOf`/`customMetricSeries`). Callers
 * resolving a goal's measured value must check for `custom_metric`
 * separately — see `resolveGoalCurrentValue`'s callers in the API and in
 * `buildSnapshot`.
 */
export const GOAL_TYPE_METRIC: Partial<Record<GoalType, MetricKey>> = {
  weight: 'weight',
  waist: 'waist',
  hip: 'hip',
  chest: 'chest',
  right_arm: 'rightArm',
  left_arm: 'leftArm',
  right_thigh: 'rightThigh',
  left_thigh: 'leftThigh',
  body_fat: 'bodyFat',
  muscle: 'muscle',
};

/** True when this goal's current value is read rather than typed. */
export function goalTracksMeasurement(goalType: GoalType): boolean {
  return GOAL_TYPE_METRIC[goalType] !== undefined || goalType === 'custom_metric';
}

/**
 * The current value a goal should show.
 *
 * `measured` is the latest measurement's value for the goal's metric, or null
 * when there is no measurement or the instructor left that field empty. A null
 * falls back to whatever was stored: an old goal keeps the number it had
 * rather than losing it because this month's measurement skipped that field.
 */
export function resolveGoalCurrentValue(
  goalType: GoalType,
  storedValue: number | null,
  measured: number | null | undefined,
): number | null {
  if (!goalTracksMeasurement(goalType)) return storedValue;
  return measured ?? storedValue;
}

export type GoalMode = 'numeric' | 'qualitative';
export type GoalStatus = 'active' | 'completed' | 'cancelled';

/**
 * A goal that cannot be tracked in cm or kg carries the instructor's own note
 * and nothing else — Doc 6 §14.3.
 *
 * An earlier version gave these goals a closed status set (Başlandı / Gelişim
 * Sürüyor / İyi Gidiyor / Hedefe Ulaşıldı). It was removed: the labels read as
 * a verdict the studio had not actually made, and an instructor who disagreed
 * with the wording had no way to say anything else. The note has no such
 * problem, because the words in it are the instructor's.
 */
export const QUALITATIVE_NOTE_MAX_LENGTH = 300;

/**
 * Numeric goal progress — Doc 6 §14.2.
 *
 *   progress = floor( clamp(0, 100, (start - current) / (start - target) * 100) )
 *
 * Direction-agnostic: correct whether the target is below the start (waist,
 * weight) or above it (muscle mass), because numerator and denominator flip
 * sign together.
 *
 * `floor`, not `round` — a goal at 99.6% is not finished, and rounding it up to
 * 100% in a document sent to a paying client overstates the result.
 *
 * Returns null when any input is missing. The card then renders the values it
 * does have, with no ring, no bar and no percentage.
 */
export function calculateGoalProgress(
  startValue: number | null | undefined,
  currentValue: number | null | undefined,
  targetValue: number | null | undefined,
): number | null {
  if (startValue == null || currentValue == null || targetValue == null) return null;
  if (!Number.isFinite(startValue) || !Number.isFinite(currentValue)) return null;
  if (!Number.isFinite(targetValue)) return null;

  const span = startValue - targetValue;

  if (span === 0) {
    // The goal was already met when it was set; it is complete only if the
    // current value still holds there.
    return currentValue === targetValue ? 100 : 0;
  }

  const achieved = startValue - currentValue;
  const ratio = (achieved / span) * 100;

  return Math.floor(Math.min(100, Math.max(0, ratio)));
}
